import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import DashboardPage from './pages/DashboardPage';
import VehiclesPage from './pages/VehiclesPage';
import DriversPage from './pages/DriversPage';
import UsersPage from './pages/UsersPage';
import AssignmentsPage from './pages/AssignmentsPage';
import TripsPage from './pages/TripsPage';
import ReportsPage from './pages/ReportsPage';
import { GoogleLogin, googleLogout } from '@react-oauth/google';
import { jwtDecode } from "jwt-decode";
import { BACKEND_URL } from './config';
import fetchWithAuth from './utils/fetchWithAuth';
import companyLogo from './assets/prisma-logo.png'; // Import the logo

const App = () => {
  const [user, setUser] = useState(null);
  const [loginError, setLoginError] = useState("");

  const handleLogout = useCallback(() => {
    googleLogout();
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('jwt');
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('jwt');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000; // to get in seconds
        if (decodedToken.exp < currentTime) {
          // Token is expired
          handleLogout();
        } else {
          // Token is valid, set user from localStorage
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            setUser(JSON.parse(storedUser));
          } else {
            // Data inconsistency: token exists but user data is missing.
            handleLogout();
          }
        }
      } catch (error) {
        // Invalid token format
        console.error("Failed to decode JWT, logging out.", error);
        handleLogout();
      }
    }
  }, [handleLogout]);

  useEffect(() => {
    window.addEventListener('auth-expired', handleLogout);
    return () => {
      window.removeEventListener('auth-expired', handleLogout);
    };
  }, [handleLogout]);

  const handleGoogleLogin = async (credentialResponse) => {
    localStorage.setItem('jwt', credentialResponse.credential);
    const decoded = jwtDecode(credentialResponse.credential);
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/users/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: decoded.email })
      });
      const data = await res.json();
      if (data.exists === true && data.role) {
        const userWithRole = { ...decoded, role: data.role };
        setUser(userWithRole);
        localStorage.setItem('user', JSON.stringify(userWithRole));
        setLoginError("");
      } else {
        if (data.exists !== false) {
          setLoginError("Su usuario está desactivado, por favor contacte al administrador");
        } else {
          setLoginError("Su usuario no está registrado, por favor contacte al administrador");
        }
        setUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('jwt');
      }
    } catch (err) {
      setLoginError("Error al verificar el usuario");
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('jwt');
    }
  };

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-gray-50">
        {!user ? (
          <div className="flex flex-col items-center justify-center h-screen p-4">
            <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-xl shadow-lg text-center">
              <img src={companyLogo} alt="Company Logo" className="w-32 h-auto mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900">
                Sistema de Gestión de Flota
              </h1>
              <p className="text-gray-600">
                Inicie sesión con su cuenta de Google para acceder al panel.
              </p>
              <div className="flex justify-center pt-4">
                <GoogleLogin
                  onSuccess={handleGoogleLogin}
                  onError={() => {
                    setLoginError('Fallo en el inicio de sesión de Google.');
                  }}
                />
              </div>
              {loginError && (
                <div className="mt-4 text-red-600 text-sm">{loginError}</div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex justify-between items-center mb-4">
                <img src={companyLogo} alt="Company Logo" className="h-10 w-auto" />
                <div className="flex items-center space-x-2">
                  <span className="text-gray-700">{user.email}</span>
                  <button
                    className="px-3 py-1 bg-red-500 text-white rounded"
                    onClick={handleLogout}
                  >
                    Cerrar sesión
                  </button>
                </div>
              </div>
              <NavTabs user={user} />
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/vehicles" element={<VehiclesPage user={user} />} />
                <Route path="/drivers" element={<DriversPage user={user} />} />
                <Route path="/users" element={<UsersPage user={user} />} />
                <Route path="/assignments" element={<AssignmentsPage user={user} />} />
                <Route path="/trips" element={<TripsPage user={user} />} />
                <Route path="/reports" element={<ReportsPage />} />
              </Routes>
            </div>
          </div>
        )}
      </div>
    </BrowserRouter>
  );
};

const NavTabs = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const tabs = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'vehicles', label: 'Vehículos' },
    { key: 'drivers', label: 'Conductores' },
    { key: 'users', label: 'Usuarios' },
    { key: 'assignments', label: 'Asignaciones' },
    { key: 'trips', label: 'Viajes' }
  ];

  if (user && (user.role === 'Administrador' || user.role === 'Supervisor')) {
    tabs.push({ key: 'reports', label: 'Reportes' });
  }

  return (
    <div className="flex space-x-4 mb-8 border-b border-gray-200 overflow-x-auto">
      {tabs.map(tab => (
        <button
          key={tab.key}
          className={`whitespace-nowrap pb-2 px-1 font-medium ${location.pathname === `/${tab.key}` ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => navigate(`/${tab.key}`)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

export default App;
